
import { useState } from "react";
import { api } from "@/lib/api";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { PageHeader, LoadingState, ErrorState } from "@/components/features/states";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  Package,
  Utensils,
  ShoppingCart,
  Pill,
  FileText,
  ListChecks,
  Droplets,
  Power,
  Clock,
} from "lucide-react";
import type { ServiceStatus } from "@/lib/types";

// Vertical → icon + label key mapping.
const VERTICAL_META: Record<
  string,
  { icon: React.ElementType; labelKey: string }
> = {
  parcel: { icon: Package, labelKey: "admin.services.verticalParcel" },
  food: { icon: Utensils, labelKey: "admin.services.verticalFood" },
  grocery: { icon: ShoppingCart, labelKey: "admin.services.verticalGrocery" },
  pharmacy: { icon: Pill, labelKey: "admin.services.verticalPharmacy" },
  courier: { icon: FileText, labelKey: "admin.services.verticalCourier" },
  errand: { icon: ListChecks, labelKey: "admin.services.verticalErrand" },
  water: { icon: Droplets, labelKey: "admin.services.verticalWater" },
};

// Vertical display order (matches the customer dashboard tile order).
const VERTICAL_ORDER = [
  "parcel",
  "food",
  "grocery",
  "pharmacy",
  "courier",
  "errand",
  "water",
];

export function AdminServices() {
  const { t } = useTranslation();
  const qc = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["admin", "services"],
    queryFn: () => api.admin.serviceStatus(),
  });
  const services: ServiceStatus[] = data?.data?.services ?? [];

  // Sort by canonical vertical order.
  const sorted = [...services].sort(
    (a, b) =>
      VERTICAL_ORDER.indexOf(a.vertical) - VERTICAL_ORDER.indexOf(b.vertical)
  );

  const toggleMutation = useMutation({
    mutationFn: (args: { id: string; enabled: boolean; reason?: string | null }) =>
      api.admin.updateServiceStatus(args.id, {
        enabled: args.enabled,
        reason: args.reason ?? undefined,
      }),
    onSuccess: (res, vars) => {
      if (!res.ok) {
        toast.error(res.error || "Failed to update service");
        return;
      }
      qc.invalidateQueries({ queryKey: ["admin", "services"] });
      qc.invalidateQueries({ queryKey: ["services", "status"] });
      const svc = res.data!.service;
      const meta = VERTICAL_META[svc.vertical];
      const label = meta ? t(meta.labelKey) : svc.vertical;
      toast.success(
        svc.enabled
          ? t("admin.services.toastEnabled", { vertical: label })
          : t("admin.services.toastDisabled", { vertical: label })
      );
    },
    onError: (e: any) => toast.error(e?.message || "Network error"),
  });

  if (isLoading) return <LoadingState />;
  if (error || !data?.ok)
    return <ErrorState message={data?.error || "Failed to load"} onRetry={refetch} />;

  return (
    <div className="space-y-5">
      <PageHeader
        title={t("admin.services.title")}
        subtitle={t("admin.services.subtitle")}
      />

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="flex items-start gap-3 p-4 text-sm">
          <Power className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
          <p className="text-muted-foreground">{t("admin.services.hint")}</p>
        </CardContent>
      </Card>

      <div className="space-y-3">
        {sorted.map((svc) => (
          <ServiceRow
            key={svc.id}
            service={svc}
            onToggle={(enabled, reason) =>
              toggleMutation.mutate({ id: svc.id, enabled, reason })
            }
            saving={
              toggleMutation.isPending &&
              toggleMutation.variables?.id === svc.id
            }
          />
        ))}
      </div>
    </div>
  );
}

function ServiceRow({
  service,
  onToggle,
  saving,
}: {
  service: ServiceStatus;
  onToggle: (enabled: boolean, reason?: string | null) => void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const meta = VERTICAL_META[service.vertical] ?? {
    icon: Package,
    labelKey: "admin.services.verticalParcel",
  };
  const Icon = meta.icon;
  const [reason, setReason] = useState(service.reason || "");

  return (
    <Card
      className={
        service.enabled
          ? ""
          : "border-amber-300 bg-amber-50/40 dark:border-amber-700/60 dark:bg-amber-950/10"
      }
    >
      <CardContent className="space-y-3 p-4">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              service.enabled
                ? "bg-primary/10 text-primary"
                : "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            }`}
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold">{t(meta.labelKey)}</p>
              <Badge variant={service.enabled ? "secondary" : "destructive"}>
                {service.enabled
                  ? t("admin.services.enabled")
                  : t("admin.services.disabled")}
              </Badge>
            </div>
            <p className="mt-0.5 flex items-center gap-1 text-[11px] text-muted-foreground">
              <Clock className="h-3 w-3" />
              {t("admin.services.lastUpdated", {
                date: new Date(service.updatedAt).toLocaleString(),
              })}
            </p>
          </div>
          <Switch
            checked={service.enabled}
            disabled={saving}
            onCheckedChange={(checked) => onToggle(checked, reason)}
            aria-label={`Toggle ${service.vertical} service`}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor={`reason-${service.id}`} className="text-xs">
            {t("admin.services.reasonLabel")}
          </Label>
          <Input
            id={`reason-${service.id}`}
            className="h-8 text-sm"
            placeholder={t("admin.services.reasonPlaceholder")}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={saving}
          />
        </div>

        {saving && (
          <p className="text-right text-xs text-muted-foreground">Saving…</p>
        )}
      </CardContent>
    </Card>
  );
}
